import {
  ApostleEmporiumSortItems,
  ApostleFilterInput,
  ApostleLite,
  ApostleSortBy
} from '@apeiron/apostle'
import { SortInput } from '@apeiron/common'
import ApostleListPagingView from '@src/components/apostles/list/ApostleListPagingView'
import { GAAssetType } from '@src/constants/googleAnalytics'
import useAposleFilterQuery from '@src/hooks/apeiron/apostle/useApostleFilterQuery'
import useTracking from '@src/hooks/common/analytics/useTracking'
import useQueryPaging from '@src/hooks/common/router/useQueryPaging'
import useGetApostles from '@src/hooks/graphql/useGetApostles'
import { ReactNode, useCallback, useEffect, useRef } from 'react'

const FetchedApostleListPublic = (props: Props) => {
  const {
    className,
    title,
    renderCard,
    defaultFilterInput,
    thirdNavBar,
    withTracking = false
  } = props

  const { gtmEmitViewItemList, gtmHandleListFilterChange } = useTracking()

  const requestParamUpdatedRef = useRef(false)

  const {
    loading: myApostlesLoading,
    fetch: fetchApostles,
    data: myApostles,
    pageInfo: myApostlesPageInfo
  } = useGetApostles()

  const queryParams = { supportUrl: true }

  const { queryPageNumber, setPageNumberQuery } = useQueryPaging(queryParams)

  const { filterInput, sortInput, setBothInput, setFilterInput, setSortInput } =
    useAposleFilterQuery(queryParams)

  const handleOnFilterOrSortChange = useCallback(
    (filter?: ApostleFilterInput, sort?: SortInput<ApostleSortBy>) => {
      if (filter && sort) {
        setBothInput(filter, sort)
      } else {
        if (filter) setFilterInput(filter)
        if (sort) setSortInput(sort)
      }

      if (withTracking && filter) {
        // GA-vAOzjsB7KKVDHqpI
        gtmHandleListFilterChange(GAAssetType.Apostle, filterInput, filter)
      }
    },
    [
      withTracking,
      setBothInput,
      setFilterInput,
      setSortInput,
      gtmHandleListFilterChange,
      filterInput
    ]
  )

  useEffect(() => {
    if (defaultFilterInput) {
      handleOnFilterOrSortChange(defaultFilterInput)
      requestParamUpdatedRef.current = true
    }
  }, [defaultFilterInput])

  useEffect(() => {
    if (requestParamUpdatedRef.current === false) {
      fetchApostles({
        pageInput: {
          page: queryPageNumber
        },
        filterInput,
        sortInput
      }).then(res => {
        if (withTracking) {
          // GA-Ks6di3aFmWJAYsI
          gtmEmitViewItemList({
            type: GAAssetType.Apostle,
            items: res.data
          })
        }
      })
    }

    requestParamUpdatedRef.current = false
  }, [
    filterInput,
    sortInput,
    queryPageNumber,
    fetchApostles,
    gtmEmitViewItemList,
    withTracking
  ])

  return (
    <ApostleListPagingView
      className={className}
      popupMode={false}
      apostles={myApostles}
      onPageNumberChange={setPageNumberQuery}
      filterInput={filterInput}
      sortInput={sortInput}
      onFilterOrSortChange={handleOnFilterOrSortChange}
      loading={myApostlesLoading}
      pageInfo={myApostlesPageInfo}
      sortByDropdownOptions={ApostleEmporiumSortItems}
      renderCard={renderCard}
      thirdNavBar={thirdNavBar}
      title={title}
    />
  )
}

type Props = {
  className?: string
  defaultFilterInput?: ApostleFilterInput
  renderCard: (apostle: ApostleLite) => ReactNode
  thirdNavBar?: ReactNode
  withTracking?: boolean
  title?: string
}

export default FetchedApostleListPublic
